if (!customElements.get('wls-collection-tiers-nav')) {
  class WlsCollectionTiersNav extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        tierNav: 'js-collection-tier-nav',
        tiersSection: 'wls-collection-tiers',
        placeholder: 'wls-collection-tiers-nav-placeholder',
        isActive: 'is-active',
        isSticky: 'is-sticky',
      };

      this.bind = {
        onScroll: this._onScroll.bind(this),
        onResize: this._onResize.bind(this),
      };

      /** @type {HTMLDivElement | null} */
      this._placeholder = null;

      /** @type {IntersectionObserver | null} */
      this._intersectionObserver = null;

      /** @type {Element | null} Cached tiers section; cleared on resize. */
      this._tiersSection = null;

      /**
       * Natural document-relative top of this element.
       * Captured before the placeholder is inserted so the rect is reliable.
       * @type {number}
       */
      this._naturalTop = 0;
    }

    /**
     * Returns key nodes scoped to this element (and the document for out-of-tree elements).
     * Re-queries on each access so callers always see the current DOM state.
     *
     * @returns {{
     *   tierNav: Element | null;
     *   navLinks: NodeListOf<Element>;
     * }}
     */
    get elements() {
      return {
        tierNav: this.querySelector(`.${this.classes.tierNav}`),
        navLinks: this.querySelectorAll(`.${this.classes.tierNav} a[href^="#"]`),
      };
    }

    /**
     * Inserts the placeholder, binds scroll/resize listeners, and sets up scroll-based active sync.
     *
     * @returns {void}
     */
    connectedCallback() {
      this._setupFixed();
      this._setupScrollSync();
      this._setupNavClicks();
    }

    /**
     * Removes scroll/resize listeners and disconnects the IntersectionObserver.
     *
     * @returns {void}
     */
    disconnectedCallback() {
      window.removeEventListener('scroll', this.bind.onScroll);
      window.removeEventListener('resize', this.bind.onResize);
      this._intersectionObserver?.disconnect();
    }

    // ─── Fixed nav ──────────────────────────────────────────────────────────

    /**
     * Returns the current bottom edge of the site header in viewport-relative px.
     * Falls back to 0 when no header is found.
     *
     * @returns {number}
     */
    _getHeaderBottom() {
      const header = document.querySelector('.section-header');
      return header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    }

    /**
     * Inserts a same-height placeholder sibling before this element so the
     * surrounding layout does not jump when the element leaves normal flow,
     * then starts listening to scroll and resize events.
     *
     * @returns {void}
     */
    _setupFixed() {
      // Capture natural position BEFORE inserting the placeholder.
      // After insertion the placeholder is display:none so its rect returns 0,
      // which would make scrolledPastTop immediately true on init.
      this._naturalTop = this.getBoundingClientRect().top + window.scrollY;

      this._placeholder = document.createElement('div');
      this._placeholder.className = this.classes.placeholder;
      this.parentNode.insertBefore(this._placeholder, this);

      // Height set once — never toggled. Visibility is controlled by the
      // `is-sticky` class (CSS: display:none ↔ display:block).
      this._placeholder.style.height = `${this.offsetHeight}px`;

      window.addEventListener('scroll', this.bind.onScroll, { passive: true });
      window.addEventListener('resize', this.bind.onResize);

      this._updateFixed();
    }

    /**
     * Scroll event handler — recalculates fixed state on every scroll tick.
     *
     * @returns {void}
     */
    _onScroll() {
      this._updateFixed();
    }

    /**
     * Resize event handler — invalidates the cached tiers section reference
     * (the element may have reflowed) then recalculates fixed state.
     *
     * @returns {void}
     */
    _onResize() {
      this._tiersSection = null;
      // Re-measure natural position only while not sticky (element is in flow).
      // When sticky the element is position:fixed and its rect is unreliable.
      if (!this.classList.contains(this.classes.isSticky)) {
        this._naturalTop = this.getBoundingClientRect().top + window.scrollY;
        this._placeholder.style.height = `${this.offsetHeight}px`;
      }
      this._updateFixed();
    }

    /**
     * Decides whether this element should be `position: fixed` based on two boundaries:
     *
     * - **Top boundary**: becomes fixed once the header has scrolled down past the
     *   element's natural document position (read from the placeholder).
     * - **Bottom boundary**: unfixes once the fixed nav would visually overlap the
     *   bottom edge of the `.wls-collection-tiers` section, preventing it from
     *   drifting over the product grid below.
     *
     * Both boundaries apply in both scroll directions (down and up).
     *
     * @returns {void}
     */
    _updateFixed() {
      const headerBottom = this._getHeaderBottom();
      const elHeight = this.offsetHeight;

      const scrolledPastTop = window.scrollY + headerBottom >= this._naturalTop;

      // Resolve the tiers section lazily and cache until next resize.
      if (!this._tiersSection) {
        this._tiersSection = document.querySelector(`.${this.classes.tiersSection}`);
      }

      let scrolledPastBottom = false;
      if (this._tiersSection && scrolledPastTop) {
        const sectionBottom =
          this._tiersSection.getBoundingClientRect().bottom + window.scrollY;
        scrolledPastBottom = window.scrollY + headerBottom + elHeight >= sectionBottom;
      }

      const shouldFix = scrolledPastTop && !scrolledPastBottom;

      if (shouldFix) {
        this.style.top = `${headerBottom}px`;
        this.classList.add(this.classes.isSticky);
        this._placeholder.classList.add(this.classes.isSticky);
      } else {
        this.classList.remove(this.classes.isSticky);
        this._placeholder.classList.remove(this.classes.isSticky);
      }
    }

    // ─── Nav click / smooth scroll ──────────────────────────────────────────

    /**
     * Returns the total fixed offset (header + this element) to subtract when
     * scrolling to an anchor. Nav height is always included: even when not yet
     * sticky, scrolling to a tier section will push the nav into sticky position,
     * so the destination must already account for its height.
     *
     * @returns {number}
     */
    _getScrollOffset() {
      return this._getHeaderBottom() + this.offsetHeight;
    }

    /**
     * Binds click handlers on all tier nav anchor links so they smooth-scroll
     * to the target element with the correct fixed-header + fixed-nav offset.
     *
     * @returns {void}
     */
    _setupNavClicks() {
      const { navLinks } = this.elements;
      navLinks.forEach((link) => {
        link.addEventListener('click', (e) => this._onNavLinkClick(e));
      });
    }

    /**
     * Click handler for tier nav anchor links.
     *
     * @param {MouseEvent} e
     * @returns {void}
     */
    _onNavLinkClick(e) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const link = e.currentTarget;
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#')) return;

      e.preventDefault();

      if (href === '#') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      let id;
      try {
        id = decodeURIComponent(href.slice(1));
      } catch {
        id = href.slice(1);
      }
      if (!id) return;

      let el;
      try {
        el = document.getElementById(id);
      } catch {
        return;
      }
      if (!el) return;

      const offset = this._getScrollOffset();
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    // ─── Scroll sync ────────────────────────────────────────────────────────

    /**
     * Sets up an IntersectionObserver that keeps the active state on tier nav
     * links in sync with whichever tier section is currently in the viewport.
     *
     * @returns {void}
     */
    _setupScrollSync() {
      const { navLinks } = this.elements;
      if (!navLinks.length) return;

      // Build a map of nav link → tier section DOM element.
      const targets = [];
      navLinks.forEach((link) => {
        const rawId = link.getAttribute('href').slice(1); // strip leading #
        let el = null;
        try {
          el =
            document.getElementById(rawId) ??
            document.getElementById(decodeURIComponent(rawId));
        } catch {
          // rawId is not a valid element id — skip silently.
        }
        if (el) targets.push({ el, link });
      });

      if (!targets.length) return;

      // Activate the first tier link by default.
      this._setActiveLink(targets[0].link, navLinks);

      this._intersectionObserver = new IntersectionObserver(
        (entries) => this._onIntersection(entries, targets, navLinks),
        {
          // Trigger when at least 200px of the tier is past the top of the viewport.
          rootMargin: '-200px 0px -60% 0px',
          threshold: 0,
        }
      );

      targets.forEach(({ el }) => this._intersectionObserver.observe(el));
    }

    /**
     * IntersectionObserver callback — activates the nav link whose tier section
     * has entered the observed viewport region.
     *
     * @param {IntersectionObserverEntry[]} entries
     * @param {{ el: Element; link: Element }[]} targets
     * @param {NodeListOf<Element>} navLinks
     * @returns {void}
     */
    _onIntersection(entries, targets, navLinks) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const match = targets.find((t) => t.el === entry.target);
        if (match) this._setActiveLink(match.link, navLinks);
      });
    }

    /**
     * Removes `is-active` from all nav links and applies it to `activeLink`.
     *
     * @param {Element} activeLink
     * @param {NodeListOf<Element>} navLinks
     * @returns {void}
     */
    _setActiveLink(activeLink, navLinks) {
      navLinks.forEach((l) => l.classList.remove(this.classes.isActive));
      activeLink.classList.add(this.classes.isActive);
    }
  }

  customElements.define('wls-collection-tiers-nav', WlsCollectionTiersNav);
}
